import { useTheme } from "@/hooks/useTheme";
import { Pressable, StyleSheet, Text, View, Platform, StyleProp, ViewStyle } from "react-native";
import { useState } from "react";
import { CardProps } from "@/components/Card";
import { MaterialIcons } from "@expo/vector-icons";
import { ShiftCard } from "@/components/ShiftCard";
import { ShiftPositionType } from "@/types/db/shift_positions";
import { ScrollView } from "react-native-gesture-handler";
import { router } from "expo-router";

type Props = {
  day?: number;
  weekday?: string;
  month?: string;
  add?: () => void;
  cards?: CardProps[];
  style?: StyleProp<ViewStyle>;
  isPast?: boolean;
  isToday?: boolean;
  hiddenCount?: number;
  issueCount?: number;
};

export default function Swimlane({
  day,
  weekday,
  month,
  cards = [],
  style,
  isToday = false,
  isPast,
  hiddenCount = 0,
  issueCount = 0,
}: Props) {
  const { colors } = useTheme();
  const [hovered, setHovered] = useState(false);

  // Pass through shift_positions as-is (already has all needed data)
  const getPositionStats = (positions: ShiftPositionType[] = []) => {
    return positions; // shift_positions already has: id, position_id, quantity, shift_users, etc.
  };

  return (
    <View
      style={[
        styles.container,
        { borderColor: colors.border, backgroundColor: colors.background },
        style,
        isPast && { opacity: 0.6 },
        isToday && { borderColor: colors.primary },
      ]}
    >
      {/* Issue badge - absolute positioned top right */}
      {/* {issueCount > 0 && ( <View style={[styles.issueBadge, { backgroundColor: colors.dangerBorder }]}> <Text style={styles.issueBadgeText}>{issueCount}</Text>{" "} </View>)} */}

      <View style={styles.header}>
        <Text style={[styles.day, { color: colors.text }, isToday && { color: colors.primary }]}>{day}</Text>
        <Text style={[styles.weekday, { color: colors.text }, isToday && { color: colors.primary }]}>
          {weekday}
          {month && <Text style={[styles.month, { color: colors.textSecondary }]}> {month}</Text>}
        </Text>
      </View>
      {!isPast && (
        <Pressable
          onHoverIn={() => Platform.OS === "web" && setHovered(true)}
          onHoverOut={() => Platform.OS === "web" && setHovered(false)}
          style={[styles.button, { backgroundColor: hovered ? colors.border : "transparent" }]}
          onPress={() => {
            router.setParams({ panel: "shift", new: "true" });
          }}
        >
          <MaterialIcons style={[{ color: colors.text }, styles.icon]} name={"add"} />
        </Pressable>
      )}
      <ScrollView>
        {cards.map((card, index) => {
          if (card.shift) {
            return (
              <ShiftCard
                key={`${card.shift.id}-${index}`}
                shift={card.shift}
                positions={getPositionStats(card.positions)}
                location={card.shift.location || card.shift.project?.location}
                onPress={card.onPress}
                compact={true}
                visibleFields={card.visibleFields}
              />
            );
          }
          // Fallback for non-shift cards
          return (
            <View
              key={index}
              style={[styles.fallbackCard, { backgroundColor: colors.card, borderColor: colors.border }]}
            >
              <Text style={[styles.cardTitle, { color: colors.text }]}>{card.message}</Text>
              <Text style={[styles.cardTime, { color: colors.textSecondary }]}>{card.time}</Text>
            </View>
          );
        })}
      </ScrollView>
      {hiddenCount > 0 && (
        <View style={[styles.hiddenBadge, { backgroundColor: colors.backgroundSecondary }]}>
          <Text style={[styles.hiddenCount, { color: colors.text }]}>{hiddenCount}</Text>
          <Text style={[styles.hiddenLabel, { color: colors.textSecondary }]}>hidden</Text>
        </View>
      )}
    </View>
  );
}
const styles = StyleSheet.create({
  container: {
    position: "relative",
    borderWidth: 2,
    borderRadius: 12,
    flex: 1,
    minWidth: 100,
    // maxWidth: 350,
    padding: 8,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 8,
  },
  day: {
    fontSize: 20,
    fontWeight: "bold",
  },
  weekday: {
    fontSize: 16,
    fontWeight: "600",
  },
  month: {
    fontSize: 14,
    fontWeight: "500",
  },
  issueBadge: {
    position: "absolute",
    top: -6,
    right: -6,
    minWidth: 18,
    height: 18,
    paddingHorizontal: 4,
    borderRadius: 9,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1,
  },
  issueBadgeText: {
    color: "white",
    fontSize: 12,
    fontWeight: "600",
    textAlign: "center",
    lineHeight: 18,
  },
  button: {
    borderRadius: 8,
    width: 30,
    height: 30,
    position: "absolute",
    top: 8,
    right: 8,
  },
  icon: {
    fontSize: 16,
    margin: "auto",
  },
  fallbackCard: {
    padding: 8,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    marginBottom: 4,
  },
  cardTitle: {
    fontSize: 12,
    fontWeight: "600",
  },
  cardTime: {
    fontSize: 10,
    marginTop: 2,
  },
  hiddenBadge: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    marginTop: 8,
  },
  hiddenCount: {
    fontSize: 13,
    fontWeight: "600",
  },
  hiddenLabel: {
    fontSize: 12,
  },
});
